import { useEffect, useRef, useState } from "react";
import { Upload, Trash2, Save, User as UserIcon, Bell, BellOff, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { registerPush } from "@/lib/push";
import { PhoneInput } from "@/components/PhoneInput";
import { useTableControls, SortableTh, FilterChip } from "@/components/SortableTable";

const BACKEND = process.env.REACT_APP_BACKEND_URL || "";

const GoldButton = ({ children, ...p }) => (
    <button {...p} className={`inline-flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground hover:bg-[#C9A537] disabled:opacity-40 text-xs font-medium uppercase tracking-[0.15em] ${p.className || ""}`}>{children}</button>
);
const GhostButton = ({ children, ...p }) => (
    <button {...p} className={`inline-flex items-center gap-2 px-4 py-2 border border-border hover:border-primary hover:text-primary disabled:opacity-40 text-xs uppercase tracking-[0.15em] ${p.className || ""}`}>{children}</button>
);

export default function Profile() {
    const { user, refresh } = useAuth();
    const [me, setMe] = useState(null);
    const [form, setForm] = useState({ name: "", phone: "" });
    const [saving, setSaving] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [photoV, setPhotoV] = useState(Date.now());
    const fileRef = useRef(null);

    const load = async () => {
        try {
            const { data } = await api.get("/me/profile");
            setMe(data);
            setForm({ name: data.name || "", phone: data.phone || "" });
        } catch (e) { toast.error(apiError(e)); }
    };
    useEffect(() => { load(); }, []);

    const save = async () => {
        if (!form.name.trim()) { toast.error("Name is required"); return; }
        setSaving(true);
        try { await api.patch("/me/profile", form); toast.success("Profile updated"); await refresh?.(); load(); }
        catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
    };

    const upload = async (e) => {
        const f = e.target.files?.[0];
        if (!f) return;
        const maxKb = me?.photo_size_caps_kb?.firm_max_kb || 2048;
        if (f.size > maxKb * 1024) {
            toast.error(`File too large (${Math.round(f.size / 1024)} KB). Max ${maxKb} KB for your firm.`);
            e.target.value = ""; return;
        }
        if (!["image/png", "image/jpeg", "image/jpg", "image/webp"].includes(f.type)) {
            toast.error("Use PNG, JPG or WEBP"); e.target.value = ""; return;
        }
        setUploading(true);
        try {
            const fd = new FormData(); fd.append("file", f);
            const { data } = await api.post("/me/photo", fd, { headers: { "Content-Type": "multipart/form-data" } });
            toast.success(`Photo uploaded (${data.size_kb} KB)`);
            setPhotoV(Date.now());
            await refresh?.(); load();
        } catch (err) { toast.error(apiError(err)); }
        finally { setUploading(false); e.target.value = ""; }
    };

    const removePhoto = async () => {
        if (!window.confirm("Remove your profile photo?")) return;
        try { await api.delete("/me/photo"); toast.success("Photo removed"); setPhotoV(Date.now()); await refresh?.(); load(); }
        catch (e) { toast.error(apiError(e)); }
    };

    if (!me) return null;
    const photoSrc = me.photo_url ? `${BACKEND}/api/users/${me.id}/photo?v=${photoV}` : "";

    return (
        <div className="space-y-6 max-w-3xl" data-testid="profile-page">
            <div>
                <div className="overline">Your account</div>
                <h1 className="display-display mt-1">My Profile</h1>
                <p className="text-sm text-muted-foreground mt-2">Upload a professional photo, update your contact details, and manage notifications.</p>
            </div>

            {/* Photo */}
            <div className="bg-card border border-border p-6 space-y-4" data-testid="profile-photo-card">
                <div className="overline">Profile photo</div>
                <div className="flex items-start gap-6 flex-wrap">
                    <div className="bg-background border border-border w-32 h-32 rounded-full overflow-hidden flex items-center justify-center" data-testid="profile-photo-preview">
                        {photoSrc ? <img src={photoSrc} alt={me.name} className="w-full h-full object-cover" data-testid="profile-photo-img"/>
                        : <UserIcon className="w-12 h-12 text-muted-foreground" strokeWidth={1.2}/>}
                    </div>
                    <div className="flex-1 min-w-[260px] space-y-3">
                        <div className="bg-background border border-primary/30 p-3 text-[11px] text-muted-foreground leading-relaxed">
                            <div className="text-primary uppercase tracking-[0.15em] text-[10px] mb-1.5">Recommended specs</div>
                            <ul className="space-y-0.5">
                                <li>• Square — <span className="text-foreground">256×256 px minimum, up to 2048×2048</span></li>
                                <li>• Clear, well-lit headshot, neutral background</li>
                                <li>• Formats: <span className="text-foreground">PNG, JPG, WEBP</span></li>
                                <li>• Maximum file size: <span className="text-foreground">{me.photo_size_caps_kb.firm_max_kb} KB</span> (firm limit)</li>
                            </ul>
                        </div>
                        <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/webp" onChange={upload} className="hidden" data-testid="photo-file-input"/>
                        <div className="flex gap-2 flex-wrap">
                            <GoldButton onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="photo-upload-btn">
                                <Upload className="w-3.5 h-3.5"/>{uploading ? "Uploading…" : (me.photo_url ? "Replace photo" : "Upload photo")}
                            </GoldButton>
                            {me.photo_url && (
                                <GhostButton onClick={removePhoto} data-testid="photo-remove-btn"><Trash2 className="w-3.5 h-3.5"/>Remove</GhostButton>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {/* Details */}
            <div className="bg-card border border-border p-6 space-y-4" data-testid="profile-details-card">
                <div className="overline">Contact details</div>
                <Field label="Name *"><Input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} data-testid="profile-name-input"/></Field>
                <Field label="Email" hint="Contact your firm owner to change your sign-in email.">
                    <Input value={me.email} disabled className="opacity-60" data-testid="profile-email-input"/>
                </Field>
                <Field label="Phone">
                    <PhoneInput value={form.phone} onChange={(v) => setForm({ ...form, phone: v })} testid="profile-phone"/>
                </Field>
                <div className="flex justify-end">
                    <GoldButton onClick={save} disabled={saving} data-testid="profile-save-btn">
                        <Save className="w-3.5 h-3.5"/>{saving ? "Saving…" : "Save details"}
                    </GoldButton>
                </div>
            </div>

            {/* Notifications */}
            <NotificationPrefs/>
            <NotificationMatrix/>
        </div>
    );
}

function NotificationMatrix() {
    const [data, setData] = useState(null);
    const [saving, setSaving] = useState(false);

    const load = async () => {
        try { const { data } = await api.get("/me/notify-prefs"); setData(data); }
        catch (e) { toast.error(apiError(e)); }
    };
    useEffect(() => { load(); }, []);

    const toggle = (key, channel) => {
        setData(d => ({
            ...d,
            prefs: { ...d.prefs, [key]: { ...d.prefs[key], [channel]: !d.prefs[key][channel] } },
        }));
    };

    const save = async () => {
        setSaving(true);
        try {
            const { data: r } = await api.patch("/me/notify-prefs", { prefs: data.prefs });
            setData(r);
            toast.success("Notification preferences saved");
        } catch (e) { toast.error(apiError(e)); }
        finally { setSaving(false); }
    };

    const matrixCtrl = useTableControls(data?.catalog || [], {
        accessors: {
            push: (ev) => (data?.prefs?.[ev.key]?.push ?? true) ? "on" : "off",
            email: (ev) => (data?.prefs?.[ev.key]?.email ?? false) ? "on" : "off",
            event: (ev) => `${ev.label} ${ev.description || ""}`,
        },
    });

    if (!data) return null;
    return (
        <div className="bg-card border border-border p-6 space-y-4" data-testid="notify-matrix-card">
            <div className="flex items-center justify-between gap-3 flex-wrap">
                <div>
                    <div className="overline">What you get notified about</div>
                    <p className="text-xs text-muted-foreground mt-1">Toggle each event by channel. Push works after you've enabled browser notifications above.</p>
                </div>
                <GoldButton onClick={save} disabled={saving} data-testid="notify-prefs-save-btn">
                    <Save className="w-3.5 h-3.5"/>{saving ? "Saving…" : "Save preferences"}
                </GoldButton>
            </div>
            <FilterChip ctrl={matrixCtrl} className="mb-2"/>
            <div className="overflow-x-auto">
                <table className="w-full text-sm">
                    <thead>
                        <tr className="text-left text-[10px] uppercase tracking-[0.15em] text-muted-foreground border-b border-border">
                            <SortableTh field="event" ctrl={matrixCtrl} className="py-2 pr-4">Event</SortableTh>
                            <SortableTh field="push" ctrl={matrixCtrl} className="py-2 px-3 text-center w-[80px]">Push</SortableTh>
                            <SortableTh field="email" ctrl={matrixCtrl} className="py-2 px-3 text-center w-[80px]">Email</SortableTh>
                        </tr>
                    </thead>
                    <tbody>
                        {matrixCtrl.rows.map(ev => {
                            const p = data.prefs[ev.key] || { push: true, email: false };
                            return (
                                <tr key={ev.key} className="border-b border-border" data-testid={`notify-row-${ev.key}`}>
                                    <td className="py-3 pr-4">
                                        <div className="text-foreground">{ev.label}</div>
                                        <div className="text-[10px] text-muted-foreground mt-0.5">{ev.description}</div>
                                    </td>
                                    <td className="py-3 px-3 text-center">
                                        <input type="checkbox" checked={p.push}
                                            onChange={() => toggle(ev.key, "push")}
                                            className="w-4 h-4 accent-primary cursor-pointer"
                                            data-testid={`notify-${ev.key}-push`}/>
                                    </td>
                                    <td className="py-3 px-3 text-center">
                                        <input type="checkbox" checked={p.email}
                                            onChange={() => toggle(ev.key, "email")}
                                            className="w-4 h-4 accent-primary cursor-pointer"
                                            data-testid={`notify-${ev.key}-email`}/>
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>
        </div>
    );
}

function NotificationPrefs() {
    const [perm, setPerm] = useState(typeof Notification !== "undefined" ? Notification.permission : "unsupported");
    const [busy, setBusy] = useState(false);

    const enable = async () => {
        setBusy(true);
        try {
            await registerPush();
            setPerm(Notification.permission);
            if (Notification.permission === "granted") {
                toast.success("Push notifications enabled");
                try { localStorage.removeItem("push_nag_dismissed_at"); }
                catch (err) { console.warn("Could not clear push_nag_dismissed_at", err); }
            } else if (Notification.permission === "denied") {
                toast.error("Browser blocked notifications. Enable them in browser settings to receive updates.");
            }
        } catch (e) { toast.error("Could not enable notifications"); }
        finally { setBusy(false); }
    };

    const label = perm === "granted" ? "Enabled" : perm === "denied" ? "Blocked by browser" : "Not enabled";
    const tone = perm === "granted" ? "text-emerald-400" : perm === "denied" ? "text-red-400" : "text-amber-400";
    return (
        <div className="bg-card border border-border p-6 space-y-3" data-testid="profile-notify-card">
            <div className="overline">Push notifications</div>
            <p className="text-xs text-muted-foreground leading-relaxed">
                Receive real-time alerts for hearings, document uploads, case updates and chat messages — even when the app is closed.
            </p>
            <div className="flex items-center justify-between gap-3 flex-wrap">
                <div className={`flex items-center gap-2 text-sm ${tone}`}>
                    {perm === "granted" ? <Bell className="w-4 h-4"/> : <BellOff className="w-4 h-4"/>}
                    <span data-testid="push-status-label">{label}</span>
                </div>
                {perm !== "granted" && perm !== "unsupported" && (
                    <GoldButton onClick={enable} disabled={busy} data-testid="enable-push-btn">
                        <ShieldCheck className="w-3.5 h-3.5"/>{busy ? "Requesting…" : "Enable push"}
                    </GoldButton>
                )}
            </div>
            {perm === "denied" && (
                <p className="text-[11px] text-muted-foreground leading-snug">
                    Your browser is blocking notifications for this site. Click the padlock icon in the address bar → Notifications → Allow, then reload.
                </p>
            )}
        </div>
    );
}

const Field = ({ label, children, hint }) => (
    <div>
        <label className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground mb-1.5 block">{label}</label>
        {children}
        {hint && <div className="text-[10px] text-muted-foreground mt-1">{hint}</div>}
    </div>
);
const Input = (p) => (
    <input {...p} className={`w-full bg-background border border-border focus:border-primary focus:ring-1 focus:ring-primary outline-none px-3 py-2 text-sm transition-colors ${p.className || ""}`}/>
);
